import axios from 'axios';
import authService from './authService';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const TOOLS_GATEWAY_PREFIX = process.env.REACT_APP_TOOLS_GATEWAY_PREFIX || '/api/tools';

function joinBaseAndPrefix(base, prefix) {
  const b = (base || '').replace(/\/+$/, '');
  let p = (prefix || '').replace(/^\/+/, '/');
  if (!p.startsWith('/')) p = `/${p}`;
  // Avoid double "/api" when base ends with "/api" and prefix starts with "/api/"
  if (b.endsWith('/api') && p.startsWith('/api/')) {
    return `${b.slice(0, -4)}${p}`;
  }
  return `${b}${p}`;
}

const api = axios.create({ baseURL: joinBaseAndPrefix(API_BASE, TOOLS_GATEWAY_PREFIX) });

api.interceptors.request.use((config) => {
  // Merge full auth headers (token + user context) for RBAC scoping on backend
  const authHeaders = authService.getAuthHeaders();
  config.headers = { ...(config.headers || {}), ...(authHeaders || {}) };
  return config;
});

// Handle 401/403 or network errors by clearing auth and redirecting to login
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const isNetworkError = !error?.response && (error?.code === 'ERR_NETWORK' || error?.code === 'ERR_CONNECTION_REFUSED' || (error?.message || '').toLowerCase().includes('network'));
    if (status === 401 || status === 403 || isNetworkError) {
      try {
        await authService.logout();
      } catch (_) {
        try { authService.clearAuth(); } catch (_) {}
      }
      try { if (typeof window !== 'undefined') window.location.href = '/pages/login'; } catch (_) {}
    }
    return Promise.reject(error);
  }
);

const rtoService = {
  // Orchestrations
  listOrchestrations: async () => (await api.get('/orchestrations')).data,
  getOrchestrationSummary: async (orchId) => (await api.get(`/orchestrations/${orchId}/summary`)).data,
  getOrchestrationResults: async (orchId) => (await api.get(`/orchestrations/${orchId}/results`)).data,
  getArtifactContent: async (artifactId) => (await api.get(`/artifacts/${artifactId}/content`)).data,

  // Planning & Execution
  dryRun: async (profile, targets, options) => (await api.post('/dry-run', { profile, targets, options })).data,
  startAndApply: async (profile, targets, options) => (await api.post('/orchestrations:start-and-apply', { profile, targets, options })).data,
  listTasks: async (orchId) => (await api.get(`/orchestrations/${orchId}/tasks`)).data,
  getTaskFacts: async (taskId) => (await api.get(`/tasks/${taskId}/facts`)).data,
  getOrchestration: async (orchId) => (await api.get(`/orchestrations/${orchId}`)).data,

  // Target check
  checkTarget: async (targets) => (await api.post('/targets:check', targets)).data,

  // Tor endpoints
  torStatus: async () => (await api.get('/anonymizer/tor/status')).data,
  torExitIP: async () => (await api.get('/anonymizer/tor/exit-ip')).data,

  // Admin Policy allow-lists
  getPolicyAllowLists: async () => (await api.get('/admin/policies/allow-lists')).data,
  updatePolicyAllowLists: async (approvedCidrs = [], approvedHosts = []) => (
    await api.put('/admin/policies/allow-lists', {
      approved_cidrs: approvedCidrs,
      approved_hosts: approvedHosts,
    })
  ).data,

  // Admin Policy block-lists
  getPolicyBlockLists: async () => (await api.get('/admin/policies/block-lists')).data,
  updatePolicyBlockLists: async (blockedCidrs = [], blockedHosts = []) => (
    await api.put('/admin/policies/block-lists', {
      blocked_cidrs: blockedCidrs,
      blocked_hosts: blockedHosts,
    })
  ).data,

  // AI Summarization
  aiSummarizeStart: async ({ orchestration_id, task_ids, include_artifact_types, include_fact_kinds, max_chars, note } = {}) => (
    await api.post('/ai/summarize', {
      orchestration_id,
      task_ids,
      include_artifact_types,
      include_fact_kinds,
      max_chars,
      note,
    })
  ).data,
  aiJobStatus: async (id) => (await api.get(`/ai/jobs/${id}`)).data,
  aiJobResults: async (id) => (await api.get(`/ai/jobs/${id}/results`)).data,

  // Admin AI Models & Pricing
  getAIModels: async () => (await api.get('/admin/ai/models')).data,
  updateAIModels: async (models = []) => (await api.put('/admin/ai/models', { models })).data,

  // Admin Organization -> Model mapping
  getOrgModelMapping: async () => (await api.get('/admin/ai/org-model-mapping')).data,
  updateOrgModelMapping: async (mapping = {}) => (await api.put('/admin/ai/org-model-mapping', { mapping })).data,

  // Admin AI usage for cost analytics
  getAIUsage: async (params = {}) => (await api.get('/admin/ai/usage', { params })).data,
};

export default rtoService;